import { saveAs } from 'file-saver'

import { mapAll, mapAsync } from '@/utils/async'
import { ensureArray } from '@/utils/collection'
import { jsonToCsv } from '@/utils/format'

export const EXPORT_LIMIT = 100
export const IMPORT_LIMIT = 25

/**
 * Build request filter
 * @param {string|array} keys - Keys
 * @param {array} entries - Entries
 */
function buildRequestFilter(keys, entries) {
	return ensureArray(keys).reduce((acc, key) => ({
		...acc,
		[key]: entries.map(entry => entry[key]),
	}), {})
}

/**
 * Build unique key
 * @param {string|array} keys - keys
 * @param {object} entry - Entry
 * @return {string} - Unique key
 */
function buildUniqueKey(keys, entry) {
	return ensureArray(keys).map(key => entry[key]).join('__')
}

/**
 * Inject replacement in maps
 * @param {array} entries - Entries
 * @param {object} maps - Replacements maps
 * @param {object} replacement - Replacement data
 * @param {object} replacement.request - Hub replacement request
 * @param {string} replacement.findKey - Key to find
 * @param {string} replacement.replaceKey - Key to replace
 * @param {string} reversed - Reversed map (for importation)
 */
async function injectReplacementInMaps(entries, maps, { request, findKey, replaceKey }, reversed) {
	if (!maps[findKey]) {
		maps[findKey] = {}
	}

	const filter = {
		[reversed ? replaceKey : '_id']: entries
			.filter(entry => !maps[findKey][entry[findKey]])
			.map(entry => entry[findKey]),
	}
	const replacementEntries = await request.field(replaceKey).get(filter)

	replacementEntries.forEach((replacementEntry) => {
		if (reversed) {
			maps[findKey][replacementEntry[replaceKey]] = replacementEntry._id
		} else {
			maps[findKey][replacementEntry._id] = replacementEntry[replaceKey]
		}
	})
}

/**
 * Inject replacements in maps
 * @param {array} entries - Entries
 * @param {object} maps - Replacements maps
 * @param {array} replacements - Replacements
 * @param {boolean} reversed - Reversed
 */
async function injectReplacementsInMaps(entries, maps, replacements, reversed = false) {
	if (!entries.length || !replacements.length) { return }
	await mapAll(
		replacements,
		async replacement => injectReplacementInMaps(entries, maps, replacement, reversed),
	)
}

/**
 * Replacement fields in entries from the replacements maps
 * @param {array} entries - Entries
 * @param {object} maps - Replacement maps
 */
function replaceFields(entries, maps) {
	const keys = Object.keys(maps)
	if (!keys) { return entries }

	return entries.map((entry) => {
		const replacements = keys.reduce((acc, key) => ({
			...acc,
			[key]: maps[key][entry[key]],
		}), {})

		return {
			...entry,
			...replacements,
		}
	})
}
/** Exports */


/**
 * Export collection
 * @todo - Add support for other types
 * @param {object} filename - Filename
 * @param {object} request - Hub request
 * @param {object} options - Options
 * @param {number} options.count - Custom total (optional)
 * @param {number} options.limit - Custom limit (optional)
 * @param {object} options.maps - Replacement maps
 * @param {array} options.replacements - Dynamic Replacements
 */
export const exportCollection = async (filename, request, {
	count: optCount,
	limit: optLimit,
	maps = {},
	replacements = [],
} = {}) => {
	const count = typeof optCount !== 'undefined' ? optCount : await request.count()
	const limit = typeof optLimit !== 'undefined' ? optLimit : EXPORT_LIMIT
	const iterations = Array.from(Array(Math.ceil(count / limit)).keys())

	// Fetch entries in batch, inject dynamic replacements and replace necessary fields
	const data = await mapAsync(iterations, async (i) => {
		const entries = await request
			.limit(limit)
			.offset(i * limit)
			.get()

		await injectReplacementsInMaps(entries, maps, replacements)
		return replaceFields(entries, maps)
	})

	// Create exporation data
	const exportData = data.reduce((acc, chunk) => [...acc, ...chunk], []).map(entry => ({
		...entry,
		_id: undefined,
	}))

	// Save as JSON
	const blob = new Blob([JSON.stringify(exportData)], { type: 'application/json;charset=utf-8' })
	saveAs(blob, filename)
}

/**
 * Export collection
 * @todo - Add support for other types
 * @param {string} filename - Filename
 * @param {string} format - Format [json, csv]
 * @param {object} request - Hub request
 * @param {object} options - Options
 * @param {number} options.limit - Custom limit (optional)
 * @param {function} options.map - Map function
 * @param {boolean} options.graphql - Whether the request is a Graphql request
 * @param {boolean} options.header - Whether the exported file has computed headers
 */
export const exportCollection2 = async (filename, format, request, {
	limit: optLimit,
	map: mapFn,
	graphql: isGraphQL = false,
	header: hasHeader = true,
} = {}) => {
	const limit = typeof optLimit !== 'undefined' ? optLimit : EXPORT_LIMIT
	let count = 0
	let page = 0
	const data = []
	do {
		const entries = ensureArray(limit
			// eslint-disable-next-line no-await-in-loop
			? await request.limit(limit).offset(page++ * limit)[isGraphQL ? 'query' : 'get']()
			// eslint-disable-next-line no-await-in-loop
			: await request[isGraphQL ? 'query' : 'get']())

		count = entries.length
		entries.forEach((entry) => {
			if (mapFn) {
				// To support entry split into multiple entries
				const formattedEntries = mapFn(entry)
				ensureArray(formattedEntries).forEach((formattedEntry) => {
					data.push(formattedEntry)
				})
				return
			}
			data.push(entry)
		})
	} while (count === limit)

	console.log(data)
	let blob
	let extension
	switch (format) {
	case 'json':
		blob = new Blob([JSON.stringify(data)], { type: 'application/json;charset=utf-8' })
		extension = 'json'
		break
	// eslint-disable-next-line no-case-declarations
	case 'csv':
		blob = new Blob([jsonToCsv(data, { hasHeader })], { type: 'text/csv;charset=utf-8' })
		extension = 'csv'
		break
	default:
		throw new Error(`Invalid format ${format}, must be one of the following: [json,csv]`)
	}
	saveAs(blob, `${filename}.${extension}`)
}

/**
 * Import collection
 * @param {array} importData - Data to import
 * @param {object} request - Request to insert content
 * @param {string|array} keys - Unique key(s)
 * @param {object} options - Options
 * @param {number} options.limit - Custom limit
 * @param {number} options.update - Update existing entries (Default: false)
 * @param {object} options.maps - Replacement maps
 * @param {array} options.replacements - Dynamic Replacements
 */
export const importCollection = async (importData, request, keys, {
	limit: optLimit,
	update = false,
	maps = {},
	replacements = [],
} = {}) => {
	const limit = typeof optLimit !== 'undefined' ? optLimit : IMPORT_LIMIT

	const iterations = Array.from(Array(Math.ceil(importData.length / limit)).keys())
	await mapAsync(iterations, async (i) => {
		// Chunk data
		const importEntries = importData.slice(i * limit, (i + 1) * limit)

		// Inject dynamic replacements in the inverse way and replace entries fields
		await injectReplacementsInMaps(importEntries, maps, replacements, true)
		const entries = replaceFields(importEntries, maps)

		// Build request filter and fetch existing entries, map them by specified key(s)
		const filter = buildRequestFilter(keys, entries)
		const existingEntries = await request.params(filter).get()
		const map = existingEntries.reduce((acc, entry) => ({
			...acc,
			[buildUniqueKey(keys, entry)]: entry._id,
		}), {})

		// Add new entries
		const newEntries = entries.filter(entry => !map[buildUniqueKey(keys, entry)])
		if (newEntries.length) {
			await request.create(newEntries)
		}

		// Added updated entries if allowed (not tested yet)
		if (update) {
			const updatedEntries = entries.filter(entry => map[buildUniqueKey(keys, entry)])
				.map(entry => ({
					...entry,
					_id: map[buildUniqueKey(keys, entry)],
				}))

			if (updatedEntries.length) {
				await request.update(updatedEntries)
			}
		}

		return i
	})
}

/**
 * Import collection V2 (using upsert)
 * @param {array} importData - Data to import
 * @param {object} request - Request to insert content
 * @param {object} options - Options
 * @param {number} options.limit - Custom limit
 * @param {number} options.upsert - Upsert entries (Default: false)
 * @param {number} options.update - Update entries only (Default: false)
 */
export const importCollection2 = async (importData, request, {
	limit: optLimit,
	upsert = false,
	update = false,
} = {}) => {
	const limit = typeof optLimit !== 'undefined' ? optLimit : IMPORT_LIMIT

	const iterations = Array.from(Array(Math.ceil(importData.length / limit)).keys())

	await mapAsync(iterations, async (i) => {
		// Chunk data
		const importEntries = importData.slice(i * limit, (i + 1) * limit)

		if (update) {
			await request.update(importEntries)
		} else if (upsert) {
			await request.upsert(importEntries)
		} else {
			await request.create(importEntries)
		}

		return i
	})
}
